import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import './DailyRevenueReport.css';
import { useUser } from '../context/UserContext';
import qs from 'qs';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';
import Swal from 'sweetalert2';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const academyToBranchMap = {
  "Main Warehouse": "38052",
  "71": "38055",
  "103": "39389",
  "99": "39390",
  "92": "39392",
  "83": "39393",
  "101": "39417",
  "70": "39964",
  "75": "39965",
  "95": "39966",
  "96": "39967",
  "97": "39968",
  "93": "39969",
  "94": "39970",
  "78": "39971",
  "80": "40075",
  "66": "40115",
  "76": "40129",
  "87": "40130",
  "72": "40131",
  "82": "40132",
  "69": "40133",
  "91": "40134",
  "64": "40135",
  "74": "40136",
  "77": "40691",
  "89": "40798",
  "90": "40808",
  "68": "40867",
  "79": "40868",
  "85": "40869",
};

const DailyRevenueReport = () => {
  const { selectedCenter } = useUser();
  const token = localStorage.getItem('auth-token');
  const { userData } = useUser();
  const [revenueData, setRevenueData] = useState([]);
  const [paymentData, setPaymentData] = useState({
    TotalRevenue: 0,
    Razorpay: 0,
    Cash: 0,
    PlayO: 0,
    VenueQR: 0,
    AppWebsite: 0,
    TotalShopRevenue: 0,
  });

  const [shopData, setShopData] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const fetchShopData = async (branchId) => {
    if (!branchId) return 0;

    setShopLoading(true);
    try {
      const requestBody = {
        fromDate: fromDateFilter.split('-').reverse().join('-'),
        toDate: toDateFilter.split('-').reverse().join('-')
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_SUMMER_VASY}?branchId=${branchId}`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY,
          }
        }
      );

      if (response.data && response.data.externalResponse && Array.isArray(response.data.externalResponse)) {
        const shopItems = response.data.externalResponse;
        setShopData(shopItems);

        const totalShopRevenue = shopItems.reduce((sum, item) => {
          return sum + (item.totalPrice || 0);
        }, 0);

        return totalShopRevenue;
      } else {
        console.warn('No shop data found in response:', response.data);
        setShopData([]);
        return 0;
      }
    } catch (error) {
      console.error('Error fetching shop data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Shop Data Error',
        text: 'Failed to fetch shop sales data',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return 0;
    } finally {
      setShopLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    let acadArray = [];

    if (userData?.userType === 'ACADEMY') {
      acadArray = academyFilter?.value
        ? [academyFilter.value]
        : (userData.academyIds || []);
    } else {
      acadArray = academyFilter?.value
        ? [academyFilter.value]
        : [];
    }

    let queryString = `startDate=${fromDateFilter}&endDate=${toDateFilter}`;
    if (acadArray.length > 0) {
      queryString = `academyIds=${acadArray.join(',')}&` + queryString;
    }

    const fullUrl = `${process.env.REACT_APP_API_URL_SPORTWISE_REVENUE}?${queryString}`;

    try {
      const response = await axios.get(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        setError(null);
        let report = response.data.data.sportwiseReport || [];

        report = report.filter(item => item.sportName?.trim());

        if (sportFilter?.label) {
          const target = sportFilter.label.toLowerCase().trim();
          report = report.filter(item =>
            item.sportName?.toLowerCase().trim() === target
          );
        }
        setRevenueData(
          report.map(item => ({
            sport: item.sportName,
            cash: `₹${item.sections.reduce((sum, s) => sum + s.cashAmount, 0)}`,
            playO: `₹${item.sections.reduce((sum, s) => sum + s.playoAmount, 0)}`,
            razorpay: `₹${item.sections.reduce((sum, s) => sum + s.razorpayAmount, 0)}`,
            venueQR: `₹${item.sections.reduce((sum, s) => sum + s.venueQrAmount, 0)}`,
            appWebsite: `₹${item.sections.reduce((sum, s) => sum + (s.appSiteAmount || 0), 0)}`,
            total: `₹${item.totalAmount}`,
            categories: item.sections.map(sec => ({
              name: sec.sectionName,
              cash: `₹${sec.cashAmount}`,
              playO: `₹${sec.playoAmount}`,
              razorpay: `₹${sec.razorpayAmount}`,
              venueQR: `₹${sec.venueQrAmount}`,
              appWebsite: `₹${sec.appSiteAmount || 0}`,
              total: `₹${sec.totalAmount}`,
            })),
          }))
        );

        const paymentSummary = report.reduce((acc, sport) => {
          sport.sections.forEach(s => {
            acc.Cash += s.cashAmount;
            acc.PlayO += s.playoAmount;
            acc.Razorpay += s.razorpayAmount;
            acc.VenueQR += s.venueQrAmount;
            acc.AppWebsite += s.appSiteAmount || 0;
            acc.TotalRevenue += s.totalAmount;
          });
          return acc;
        }, { TotalRevenue: 0, Cash: 0, PlayO: 0, Razorpay: 0, VenueQR: 0, AppWebsite: 0, TotalShopRevenue: 0 });

        if (academyFilter?.value) {
          const branchId = academyToBranchMap[academyFilter.value] || academyFilter.value;
          const shopRevenue = await fetchShopData(branchId);
          paymentSummary.TotalShopRevenue = shopRevenue;
        }

        setPaymentData(paymentSummary);

      } else {
        setError("Failed to fetch data");
      }

    } catch (err) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };



  const categoryOrder = [
    'PlayO Booking',
    'Walk-In',
    'Bulk Booking',
    'Rental Sales',
    'Coaching Sales',
    'SummerCamp Sales',
    'Playing Membership Sales',
    'Tournament Sales',
    'Others',
    'Bulk Booking Sales'
  ];

  const [academyFilter, setAcademyFilter] = useState(null);
  const [sportFilter, setSportFilter] = useState(null);
  const roleDaysLimit = {
    "centre manager": 1,
    "rotational centre manager": 1,
    "default": null,
  };
  function getDaysLimit(userData) {
    const roles = (userData?.roles || []).map(r => r.toLowerCase());
    for (let role in roleDaysLimit) {
      if (roles.includes(role)) {
        return roleDaysLimit[role];
      }
    }
    return roleDaysLimit.default;
  }
  const daysAllowed = getDaysLimit(userData);
  const today = new Date();
  const pastLimit = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (daysAllowed) {
    pastLimit.setDate(today.getDate() - daysAllowed);
  }

  const formattedToday = today.toISOString().split('T')[0];
  const formattedTomorrow = tomorrow.toISOString().split('T')[0];
  const formattedPastLimit = daysAllowed
    ? pastLimit.toISOString().split('T')[0]
    : ""; 

  const [fromDateFilter, setFromDateFilter] = useState(formattedToday);
  const [toDateFilter, setToDateFilter] = useState(formattedTomorrow);


  const [academies, setAcademies] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [sportOptions, setSportOptions] = useState([]);
  const [allSports, setAllSports] = useState([]);

  useEffect(() => {
    const fetchAcademies = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const fetchedAcademies = response.data.data.academies;

        let filteredAcademies = fetchedAcademies;

        if (userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)) {
          filteredAcademies = fetchedAcademies.filter((academy) =>
            userData.academyIds.includes(academy.id)
          );
        }

        setAcademies(filteredAcademies);

        const options = filteredAcademies.map((academy) => ({
          label: academy.name,
          value: academy.id
        }));
        setAcademyOptions(options);

        const allFetchedSports = [];
        fetchedAcademies.forEach((academy) => {
          if (academy.sports) {
            academy.sports.forEach((sport) => {
              if (!allFetchedSports.some((s) => s.value === sport.sport_id)) {
                allFetchedSports.push({
                  label: sport.name,
                  value: sport.sport_id,
                  image: sport.sport_pic
                });
              }
            });
          }
        });
        setAllSports(allFetchedSports);
      } catch (error) {
        console.error('Error fetching academies:', error);
      }
    };


    fetchAcademies();
  }, []);
  useEffect(() => {

    if (academyOptions.length > 0 && selectedCenter) {
      const defaultAcademy = academyOptions.find(opt => opt.value == selectedCenter);

      if (defaultAcademy) {
        setAcademyFilter(defaultAcademy);

        const selected = academies.find((a) => a.id == selectedCenter);

        if (selected && selected.sports) {
          const sports = selected.sports.map((sport) => ({
            label: sport.name,
            value: sport.sport_id,
            image: sport.sport_pic
          }));
          setSportOptions(sports);
        } else {
          Swal.fire({
            title: 'Error!',
            text: 'No sports found for selected academy',
            icon: 'error',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } else {
        Swal.fire({
          title: 'Error!',
          text: 'No matching defaultAcademy found in options',
          icon: 'error',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }
  }, [academyOptions, selectedCenter]);
  const hasFetchedOnce = React.useRef(false);

  useEffect(() => {
    if (!hasFetchedOnce.current && academyFilter?.value && fromDateFilter && toDateFilter) {
      fetchData();
      fetchKpiData();
      hasFetchedOnce.current = true;
    }
  }, [academyFilter, fromDateFilter, toDateFilter, sportFilter]);


  const handleAcademyFilterChange = (selectedOption) => {
    setAcademyFilter(selectedOption);
    setSportFilter(null);

    if (selectedOption) {
      const selected = academies.find((a) => a.id === selectedOption.value);
      if (selected && selected.sports) {
        const sports = selected.sports.map((sport) => ({
          label: sport.name,
          value: sport.sport_id,
          image: sport.sport_pic
        }));
        setSportOptions(sports);
      } else {
        setSportOptions([]);
      }
    } else {
      setSportOptions(allSports);
    }
  };


  const handleFromDateChange = (e) => {
    setFromDateFilter(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDateFilter(e.target.value);
  };




  const [kpiData, setKpiData] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [coachingSalesData, setCoachingSalesData] = useState([]);
  const [membershipSalesData, setMembershipSalesData] = useState([]);

  const [areAllOpen, setAreAllOpen] = useState(false);
  const toggleAllWidgets = () => {
    setAreAllOpen((prev) => !prev);
  };
  const [coachingOpenStates, setCoachingOpenStates] = useState({
    enquiries: false,
    trials: false,
    enrollments: false,
    amount: false,
  });
  const [sportwiseUtilization, setSportwiseUtilization] = useState({
    enquiries: false,
    trials: false,
    enrollments: false,
    amount: false,
  });
  const [membershipOpenStates, setMembershipOpenStates] = useState({
    enquiries: false,
    trials: false,
    enrollments: false,
    amount: false,
  }); const toggleCoachingDropdown = (key) => {
    setCoachingOpenStates((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleMembershipDropdown = (key) => {
    setMembershipOpenStates((prev) => ({ ...prev, [key]: !prev[key] }));
  };


  const fetchKpiData = async () => {
    try {
      let acadArray = [];

      if (userData?.userType === 'ACADEMY') {
        acadArray = academyFilter?.value
          ? [academyFilter.value]
          : (userData.academyIds || []);
      } else {
        acadArray = academyFilter?.value
          ? [academyFilter.value]
          : [];
      }

      let queryParams = `startDate=${fromDateFilter}&endDate=${toDateFilter}`;

      if (acadArray.length > 0) {
        queryParams = `academyId=${acadArray.join(',')}&` + queryParams;
      }

      if (sportFilter?.value) {
        queryParams += `&sportId=${sportFilter.value}`;
      }

      const fullUrl = `${process.env.REACT_APP_API_URL_KPI}?${queryParams}`;

      const response = await axios.get(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        const apiData = response.data.data;

        setCoachingSalesData(apiData.coachingSales || []);
        setMembershipSalesData(apiData.membershipSales || []);

        const totalSummary = {
          TotalTrialsBooked: 0,
          TotalCoachingEnrollments: 0,
          TotalPlayingMembershipSales: 0,
          TotalSummerCampEnrollments: 0,
          TotalEnquiries: 0
        };

        apiData.coachingSales?.forEach(item => {
          totalSummary.TotalTrialsBooked += item.trialsBooked || 0;
          totalSummary.TotalCoachingEnrollments += item.enrollments || 0;
          totalSummary.TotalEnquiries += item.enquiries || 0;
          totalSummary.TotalSummerCampEnrollments += item.summerCampEnrollments || 0;
        });

        apiData.membershipSales?.forEach(item => {
          totalSummary.TotalPlayingMembershipSales += item.enrollments || 0;
          totalSummary.TotalEnquiries += item.enquiries || 0;
        });

        setKpiData({
          totalSummary,
          sportWiseSummary: [],
        });
      }

    } catch (error) {
      console.error("Error fetching KPI data:", error);
    }
  };



  const [isCoachingExpanded, setIsCoachingExpanded] = useState(false);
  const [isMembershipExpanded, setIsMembershipExpanded] = useState(false);
  const toggleCoachingDropdowns = () => {
    setIsCoachingExpanded(prev => !prev);
  };

  const toggleMembershipDropdowns = () => {
    setIsMembershipExpanded(prev => !prev);
  };


  const getAggregatedSummary = (data) => {
    return data.reduce(
      (acc, sport) => {
        acc.enquiries += sport.enquiries || 0;
        acc.trials += sport.trialsBooked || 0;
        acc.enrollments += sport.enrollments || 0;
        acc.amount += sport.totalAmount || 0;
        return acc;
      },
      { enquiries: 0, trials: 0, enrollments: 0, amount: 0 }
    );
  };

  const coachingSummary = getAggregatedSummary(coachingSalesData);
  const membershipSummary = getAggregatedSummary(membershipSalesData);

  const [isLoading, setIsLoading] = useState(false);
  const [utilizationData, setUtilizationData] = useState({});
  const [hoursMap, setHoursMap] = useState({});

  const fetchUtilizationData = async () => {
    setIsLoading(true);
    let acadArray = [];
    if (userData?.userType === 'ACADEMY') {
      acadArray = academyFilter?.value
        ? [academyFilter.value]
        : (userData.academyIds || []);
    } else {
      acadArray = academyFilter?.value
        ? [academyFilter.value]
        : [];
    }

    const commaList = acadArray.join(',');
    const queryString = `academyId=${commaList}&startDate=${fromDateFilter}&endDate=${toDateFilter}`;
    const fullUrl = `${process.env.REACT_APP_API_URL_GET_UTILISATION2}?${queryString}`;

    let hoursMap = {};

    try {
      const utilRes = await axios.get(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });


      const { sportNameMap, sportSourceWiseUtilization } = utilRes.data.data;
      Object.entries(sportNameMap).forEach(([id, name]) => {
        if (!name) return;
        const key = name.trim().toLowerCase();
        if (key === 'swimming') return;

        const u = sportSourceWiseUtilization[id] || {};

        const hrs = u.courtWiseUtilizationForPlaying || 0;

        hoursMap[key] = hrs;
      });

      setHoursMap(hoursMap);
    } catch (err) {
      setError('❌ Util fetch failed:', err);
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    fetchUtilizationData();
  }, [academyFilter, fromDateFilter, toDateFilter]);


  const handleCopyReport = async () => {
    let centreNames;
    if (academyFilter?.label) {
      centreNames = academyFilter.label;
    } else if (userData.userType === 'ACADEMY') {
      const allowedIds = userData.academyIds || [];
      centreNames = academies
        .filter(a => allowedIds.includes(a.id))
        .map(a => a.name)
        .join(', ') || 'N/A';
    } else if (userData.userType === 'ADMIN') {
      centreNames = 'All Centres';
    } else {
      centreNames = academies.map(a => a.name).join(', ') || 'N/A';
    }

    const formatDate = dateStr => {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatDuration = hrs => {
      const totalMin = Math.round(hrs * 60);
      const h = Math.floor(totalMin / 60), m = totalMin % 60;
      if (h && m) return `${h}h ${m}min`;
      if (h) return `${h}h`;
      return `${m}min`;
    };

    let totalLearnRevenue = 0;
    const learnSummaryText = coachingSalesData.map(s => {
      const enrollments = s.enrollments || 0;
      const summerCamp = s.summerCampEnrollments || 0;
      const renewals = s.renewals || 0;
      const baseAmount = s.totalAmount || 0;
      const summerCampRevenue = summerCamp * 500;
      const renewalRevenue = renewals * 500;
      const totalLearn = baseAmount + summerCampRevenue + renewalRevenue;
      totalLearnRevenue += totalLearn;
      return `${s.sportName} – ₹${totalLearn.toLocaleString()}
• Enrollment: ${enrollments} (₹${baseAmount.toLocaleString()})
• Summer Camp: ${summerCamp} (₹${summerCampRevenue.toLocaleString()})
• Renewal: ${renewals} (₹${renewalRevenue.toLocaleString()})`;
    }).join('\n\n');

    let totalPlayRevenue = 0;
    const bookingRevenueText = revenueData
      .map(sport => {
        const key = sport.sport.trim().toLowerCase();
        const rev = sport.categories
          .filter(cat => ['PlayO Booking', 'Walk-In', 'Bulk Booking'].includes(cat.name))
          .reduce((sum, c) => sum + (parseInt(c.total.replace(/[₹,]/g, ''), 10) || 0), 0);
        if (!rev) return '';
        totalPlayRevenue += rev;
        if (key === 'swimming') return `${sport.sport} – ₹${rev.toLocaleString()}`;
        const dur = formatDuration(hoursMap[key] || 0);
        return `${sport.sport} – ${dur} – ₹${rev.toLocaleString()}`;
      })
      .filter(Boolean)
      .join('\n');

    const totalMembershipSales = membershipSummary.amount || 0;
    totalPlayRevenue += totalMembershipSales;
    const membershipSalesText = totalMembershipSales
      ? `Membership Sales – ₹${totalMembershipSales.toLocaleString()}`
      : '';

    const totalShopRevenue = paymentData.TotalShopRevenue || 0;

    const dateText = fromDateFilter === toDateFilter
      ? `*Date:* ${formatDate(fromDateFilter)}`
      : `*From:* ${formatDate(fromDateFilter)}\n*To:* ${formatDate(toDateFilter)}`;

    const grandTotal = totalLearnRevenue + totalPlayRevenue + totalShopRevenue;

    const reportText = `
*Centre:* ${centreNames}
${dateText}

*Total Revenue:* ₹${grandTotal.toLocaleString()}  
----------------------------------------
*Learn*

${learnSummaryText || 'No data'}

*Total Learn Revenue:* ₹${totalLearnRevenue.toLocaleString()}
----------------------------------------
*Play*

${bookingRevenueText || 'No data'}

${membershipSalesText || 'No Membership Sales'}

*Total Play Revenue:* ₹${totalPlayRevenue.toLocaleString()}
----------------------------------------
*Shop Sales*

Total Shop Revenue: ₹${totalShopRevenue.toLocaleString()}

*View detailed report here 👉 https://newadmin.machaxi.com/dailyRevenueReport*
`;


    setIsLoading(true);
    navigator.clipboard.writeText(reportText)
      .then(() => {
        setIsLoading(false);
        Swal.fire({
          icon: 'success',
          title: 'Copied!',
          text: 'Report copied to clipboard!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      })
      .catch(err => {
        setIsLoading(false);
        Swal.fire({
          icon: 'error',
          title: 'Copy Failed',
          text: 'Unable to copy report to clipboard.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      });
  };






  return (
    <div className="enrollment-container">
      <div className="sales-filters-login" style={{ marginBottom: '10px' }}>
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${academyFilter ? "has-value" : ""}`}
            id="academyFilter"
            options={academyOptions}
            value={academyFilter}
            onChange={handleAcademyFilterChange}
            placeholder='All Academy'
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {academyFilter && <label htmlFor="academyFilter">Academy</label>}
        </div>


        <div className="floating-select">
          <Select
            isClearable
            isSearchable
            className={`sales-filter-court-booking ${sportFilter ? "has-value" : ""}`}
            id="sportFilter"
            options={sportOptions}
            value={sportFilter}
            onChange={setSportFilter}
            placeholder='Sports'
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />

          {sportFilter && <label htmlFor="sportFilter">Sport</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${fromDateFilter ? "has-value" : ""}`}
            value={fromDateFilter}
            onChange={handleFromDateChange}
            onClick={(e) => e.target.showPicker()}
            min={daysAllowed ? formattedPastLimit : ""}
            max={formattedTomorrow}
            id="fromDateFilter"
          />
          {fromDateFilter && <label htmlFor="fromDateFilter">From Date</label>}
        </div>

        {/* End Date */}
        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${toDateFilter ? "has-value" : ""}`}
            value={toDateFilter}
            onChange={handleToDateChange}
            onClick={(e) => e.target.showPicker()}
            min={fromDateFilter}
            max={formattedTomorrow}
            id="toDateFilter"
          />
          {toDateFilter && <label htmlFor="toDateFilter">To Date</label>}
        </div>
        <div className="floating-select" style={{ maxWidth: '38px' }}>
          <button
            className="search-button"
            onClick={() => {
              fetchData();
              fetchKpiData();
              fetchUtilizationData();
            }}
            style={{
              padding: '8px',
              height: '38px',
              backgroundColor: '#003b72',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Search"
          >
            <FiSearch size={18} />
          </button>
        </div>
        <div className="floating-select" style={{ maxWidth: '38px', position: 'relative' }}>
          <button
            className="copy-report-button"
            onClick={handleCopyReport}
            disabled={loading}
            title="Copy Report"
            style={{
              height: '38px',
              backgroundColor: '#003b72',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
              transition: 'background-color 0.3s ease, transform 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <span className="copy-report-tooltip">Copy to clipboard</span>
            <span>
              <svg
                viewBox="0 0 6.35 6.35"
                height="20"
                width="20"
                className="clipboard"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M2.43.265c-.3 0-.548.236-.573.53h-.328a.74.74 0 0 0-.735.734v3.822a.74.74 0 0 0 .735.734H4.82a.74.74 0 0 0 .735-.734V1.529a.74.74 0 0 0-.735-.735h-.328a.58.58 0 0 0-.573-.53zm0 .529h1.49c.032 0 .049.017.049.049v.431c0 .032-.017.049-.049.049H2.43c-.032 0-.05-.017-.05-.049V.843c0-.032.018-.05.05-.05zm-.901.53h.328c.026.292.274.528.573.528h1.49a.58.58 0 0 0 .573-.529h.328a.2.2 0 0 1 .206.206v3.822a.2.2 0 0 1-.206.205H1.53a.2.2 0 0 1-.206-.205V1.529a.2.2 0 0 1 .206-.206z"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      {/* Coaching Sales Widget */}
      <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', margin: '2px', marginBottom: '16px', overflow: 'scroll' }}>
        <h1 style={{ marginBottom: '16px', marginTop: '0px', fontSize: '20px' }}>Coaching Sales</h1>
        <div className="widgets-container" style={{ margin: '0', padding: '0' }}>
          <div className="widget-card membership-sales" onClick={toggleCoachingDropdowns}>
            <div className="widget-header">
              <div className="widget-title">Amount</div>
              <div className="widget-number" style={{ fontSize: '25px' }}>₹{getAggregatedSummary(coachingSalesData).amount.toLocaleString()}</div>
              <div className={`widget-arrow ${coachingOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isCoachingExpanded && (
              <div className="widget-dropdown">
                {coachingSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> ₹{sport.totalAmount.toLocaleString()}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Coaching Enquiries */}
          <div className="widget-card enquiries" onClick={toggleCoachingDropdowns}>
            <div className="widget-header">
              <div className="widget-title">Enquiries</div>
              <div className="widget-number">{getAggregatedSummary(coachingSalesData).enquiries}</div>
              <div className={`widget-arrow ${coachingOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isCoachingExpanded && (
              <div className="widget-dropdown">
                {coachingSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> {sport.enquiries}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Coaching Trials */}
          <div className="widget-card trials-booked" onClick={toggleCoachingDropdowns}>
            <div className="widget-header">
              <div className="widget-title">Trials</div>
              <div className="widget-number">{getAggregatedSummary(coachingSalesData).trials}</div>
              <div className={`widget-arrow ${coachingOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isCoachingExpanded && (
              <div className="widget-dropdown">
                {coachingSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> {sport.trialsBooked}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Coaching Enrollments */}
          <div className="widget-card coaching-enrollments" onClick={toggleCoachingDropdowns}>
            <div className="widget-header">
              <div className="widget-title">Enrollments</div>
              <div className="widget-number">{getAggregatedSummary(coachingSalesData).enrollments}</div>
              <div className={`widget-arrow ${coachingOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isCoachingExpanded && (
              <div className="widget-dropdown">
                {coachingSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> {sport.enrollments}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Membership Sales Widget */}
      <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', margin: '2px', marginBottom: '16px', overflow: 'scroll' }}>
        <h1 style={{ marginBottom: '16px', marginTop: '0px', fontSize: '20px' }}>Membership Sales</h1>
        <div className="widgets-container" style={{ margin: '0', padding: '0' }}>
          {/* Membership Amount */}
          <div
            className="widget-card membership-sales"
            onClick={toggleMembershipDropdowns}

          >
            <div className="widget-header">
              <div className="widget-title">Amount</div>
              <div className="widget-number" style={{ fontSize: '25px' }}>
                ₹{getAggregatedSummary(membershipSalesData).amount.toLocaleString()}
              </div>
              <div className={`widget-arrow ${membershipOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isMembershipExpanded && (
              <div className="widget-dropdown">
                {membershipSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> ₹{sport.totalAmount.toLocaleString()}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Membership Enquiries */}
          <div
            className="widget-card enquiries"
            onClick={toggleMembershipDropdowns}

          >
            <div className="widget-header">
              <div className="widget-title">Enquiries</div>
              <div className="widget-number">
                {getAggregatedSummary(membershipSalesData).enquiries}
              </div>
              <div className={`widget-arrow ${membershipOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isMembershipExpanded && (
              <div className="widget-dropdown">
                {membershipSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> {sport.enquiries}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Membership Trials */}
          <div
            className="widget-card trials-booked"
            onClick={toggleMembershipDropdowns}
          >
            <div className="widget-header">
              <div className="widget-title">Trials</div>
              <div className="widget-number">
                {getAggregatedSummary(membershipSalesData).trials}
              </div>
              <div className={`widget-arrow ${membershipOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isMembershipExpanded && (
              <div className="widget-dropdown">
                {membershipSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> {sport.trialsBooked}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Membership Enrollments */}
          <div
            className="widget-card coaching-enrollments"
            onClick={toggleMembershipDropdowns}

          >
            <div className="widget-header">
              <div className="widget-title">Sales Count</div>
              <div className="widget-number">
                {getAggregatedSummary(membershipSalesData).enrollments}
              </div>
              <div className={`widget-arrow ${membershipOpenStates.enquiries ? 'open' : ''}`}>&#9662;</div>
            </div>
            {isMembershipExpanded && (
              <div className="widget-dropdown">
                {membershipSalesData.map(sport => (
                  <div key={sport.sportId} className="widget-dropdown-item">
                    <strong>{sport.sportName}:</strong> {sport.enrollments}
                  </div>
                ))}
              </div>
            )}
          </div>


        </div>
      </div>

      {/* Shop Sales Widget */}
      <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', margin: '2px', marginBottom: '16px', overflow: 'scroll' }}>
        <h1 style={{ marginBottom: '16px', marginTop: '0px', fontSize: '20px' }}>Shop Sales</h1>
        <div className="widgets-container" style={{ margin: '0', padding: '0' }}>
          <div className="widget-card membership-sales">
            <div className="widget-header">
              <div className="widget-title">Total Shop Revenue</div>
              <div className="widget-number" style={{ fontSize: '25px' }}>
                ₹{paymentData.TotalShopRevenue.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="widget-card enquiries">
            <div className="widget-header">
              <div className="widget-title">Total Items Sold</div>
              <div className="widget-number">
                {shopData.reduce((sum, item) => sum + (item.qty || 0), 0)}
              </div>
            </div>
          </div>
          <div className="widget-card trials-booked">
            <div className="widget-header">
              <div className="widget-title">Unique Products</div>
              <div className="widget-number">
                {[...new Set(shopData.map(item => item.itemCode))].length}
              </div>
            </div>
          </div>
        </div>

      </div>

      <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', margin: '2px', marginBottom: '16px', overflow: 'scroll' }}>
        <h1 style={{ marginBottom: '16px', marginTop: '0px', fontSize: '20px' }}>SportWise Revenue</h1>

        <div className="widgets-container" style={{ margin: '0px', padding: '0px' }}>
          {/* Total Card */}
          <div className="widget-card trials-booked">
            <div
              className="widget-header"
              style={{ display: 'flex', flexDirection: 'column', gap: '0px', alignItems: 'center' }}
            >
              <div className="widget-title" style={{ fontSize: '20px', fontWeight: '600' }}>Total</div>
              <div className="widget-number" style={{ fontSize: '22px' }}>
                ₹{paymentData.TotalRevenue.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Individual Sport Cards */}
          {revenueData.map((item, index) => {
            const colorClasses = [
              'coaching-enrollments',
              'membership-sales',
              'summer-camp',
              'enquiries',
              'trials-booked',
            ];
            const assignedClass = colorClasses[index % colorClasses.length];

            return (
              <div key={index} className={`widget-card ${assignedClass}`}>
                <div
                  className="widget-header"
                  style={{ display: 'flex', flexDirection: 'column', gap: '0px', alignItems: 'center' }}
                >
                  <div className="widget-title" style={{ fontSize: '18px' }}>{item.sport}</div>
                  <div className="widget-number" style={{ fontSize: '22px' }}>{item.total}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>


      <div style={{ background: ' #ffffff', padding: '20px', borderRadius: '12px', margin: '2px', overflow: 'scroll' }}>
        <h1 style={{ marginBottom: '16px', marginTop: '0px', fontSize: '20px' }}>Revenue by Payment methods</h1>

        <div className="widgets-container" style={{ margin: '0', padding: '0' }}>

          {/* Payment Methods */}
          {Object.entries(paymentData)
            .filter(([key]) => key !== 'TotalRevenue')
            .map(([key, value], index) => {
              const paymentLabels = {
                Razorpay: 'Razorpay',
                Cash: 'Cash',
                PlayO: 'PlayO',
                VenueQR: 'Venue QR',
                AppWebsite: 'App/Website',
              };

              const colorClasses = [
                'coaching-enrollments',
                'membership-sales',
                'summer-camp',
                'enquiries',
                'trials-booked',
              ];
              const assignedClass = colorClasses[index % colorClasses.length];

              return (
                <div key={key} className={`widget-card ${assignedClass}`}>
                  <div className="widget-header" style={{ display: 'flex', flexDirection: 'column', gap: '0px', alignItems: 'center' }}>
                    <div className="widget-title" style={{ fontSize: '18px' }}>{paymentLabels[key]}</div>
                    <div className="widget-number" style={{ fontSize: '25px' }}>₹{value.toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && revenueData.length === 0 && (
        <div className="error-message">No data available</div>
      )}
      
      <div className="tt-table-container">
        <table className="tt-table" >
          <thead>
            <tr>
              <th>Category</th>
              <th>PlayO</th>
              <th>App/Website</th>
              <th>Venue QR</th>
              <th>Cash</th>
              <th>Razorpay</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.map((sport, index) => {
              const bookingNames = ['PlayO Booking', 'Walk-In', 'Bulk Booking'];
              const parseAmount = (str) => parseInt(str?.replace(/[₹,]/g, '') || 0);

              const getGroupTotal = (group, key) =>
                group.reduce((sum, cat) => sum + parseAmount(cat[key]), 0);

              const cleanedCategories = sport.categories?.map(cat => ({
                ...cat,
                name: cat.name.trim()
              })) || [];

              const bookingCategories = cleanedCategories.filter(cat =>
                bookingNames.includes(cat.name)
              );

              const salesCategories = cleanedCategories.filter(cat =>
                !bookingNames.includes(cat.name)
              );

              const renderTotalsRow = (group) => {
                const colTotal = (key) => `₹${getGroupTotal(group, key).toLocaleString()}`;
                return (
                  <tr className="category-group-total">
                    <td></td>
                    <td><strong>{colTotal('playO')}</strong></td>
                    <td><strong>{colTotal('appWebsite')}</strong></td>
                    <td><strong>{colTotal('venueQR')}</strong></td>
                    <td><strong>{colTotal('cash')}</strong></td>
                    <td><strong>{colTotal('razorpay')}</strong></td>
                    <td><strong>{colTotal('total')}</strong></td>
                  </tr>
                );
              };

              return (
                <React.Fragment key={index}>
                  {/* Sport Summary Row */}
                  <tr className="main-category sport-section-start">
                    <td className="price">{sport.sport}</td>
                    <td className="price">{sport.playO}</td>
                    <td className="price">{sport.appWebsite}</td>
                    <td className="price">{sport.venueQR}</td>
                    <td className="price">{sport.cash}</td>
                    <td className="price">{sport.razorpay}</td>
                    <td className="price main-total">{sport.total}</td>
                  </tr>

                  {/* Booking Revenue */}
                  {bookingCategories.length > 0 && (
                    <>
                      <tr className="category-group-heading">
                        <td colSpan="6" style={{ paddingLeft: '20px' }}>
                          <strong>Booking Revenue</strong>
                        </td>
                        <td>
                        </td>
                      </tr>
                      {bookingCategories.map((sub, subIndex) => (
                        <tr key={`booking-${subIndex}`}>
                          <td>{sub.name}</td>
                          <td>{sub.playO}</td>
                          <td>{sub.appWebsite}</td>
                          <td>{sub.venueQR}</td>
                          <td>{sub.cash}</td>
                          <td>{sub.razorpay}</td>
                          <td>{sub.total}</td>
                        </tr>
                      ))}
                      {renderTotalsRow(bookingCategories)}
                    </>
                  )}

                  {/* Sales Revenue */}
                  {salesCategories.length > 0 && (
                    <>
                      <tr className="category-group-heading">
                        <td colSpan="6" style={{ paddingLeft: '20px' }}><strong>Sales Revenue</strong></td>
                        <td>
                        </td>
                      </tr>
                      {salesCategories
                        .slice()
                        .sort((a, b) =>
                          categoryOrder.indexOf(a.name.trim()) - categoryOrder.indexOf(b.name.trim())
                        )
                        .map((sub, subIndex) => (
                          <tr key={`sales-${subIndex}`}>
                            <td>{sub.name}</td>
                            <td>{sub.playO}</td>
                            <td>{sub.appWebsite}</td>
                            <td>{sub.venueQR}</td>
                            <td>{sub.cash}</td>
                            <td>{sub.razorpay}</td>
                            <td>{sub.total}</td>
                          </tr>
                        ))}
                      {renderTotalsRow(salesCategories)}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>


    </div>
  );
}

export default DailyRevenueReport;